import { Link } from 'react-router-dom';
import { useState } from 'react';
import { mangas, categories, faqs, testimonials } from '@/mocks/manga';

function StarRating({ rating }: { rating: number }) {
  const fullStars = Math.floor(rating);
  const hasHalf = rating - fullStars >= 0.5;
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: fullStars }).map((_, i) => (
        <i key={`full-${i}`} className="ri-star-fill text-accent-500 text-xs"></i>
      ))}
      {hasHalf && <i className="ri-star-half-fill text-accent-500 text-xs"></i>}
      {Array.from({ length: 5 - fullStars - (hasHalf ? 1 : 0) }).map((_, i) => (
        <i key={`empty-${i}`} className="ri-star-line text-accent-500 text-xs"></i>
      ))}
    </div>
  );
}

export default function Home() {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  const featuredMangas = mangas.filter((m) => m.rating >= 9.5).slice(0, 6);
  const latestMangas = [...mangas].sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime()).slice(0, 6);

  return (
    <>
      {/* Schema.org JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "樱花动漫网",
            "url": "https://www.yinghuadongman.com/",
            "description": "韩漫下拉式在线阅读平台，提供免费韩漫资源，流畅的下拉式阅读体验。",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://www.yinghuadongman.com/search?q={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          })
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "樱花动漫网",
            "url": "https://www.yinghuadongman.com/",
            "description": "专注韩漫下拉式在线阅读的中文漫画平台",
            "foundingDate": "2024",
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "customer service",
              "email": "contact@yinghuadongman.com"
            }
          })
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "樱花动漫网 - 韩漫下拉式在线阅读平台",
            "description": "来到樱花动漫网的韩漫阅读平台，您能免费进行下拉式在线阅读韩漫，畅享海量资源。",
            "url": "https://www.yinghuadongman.com/",
            "isPartOf": { "@id": "https://www.yinghuadongman.com/#website" }
          })
        }}
      />

      {/* Hero Section */}
      <section className="relative w-full min-h-[600px] md:min-h-[700px] flex items-center justify-center overflow-hidden" aria-labelledby="hero-heading">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Stylized%20Korean%20manhwa%20illustration%20with%20dramatic%20composition%20of%20floating%20cherry%20blossom%20petals%20against%20a%20deep%20dark%20backdrop%2C%20ethereal%20glowing%20particles%2C%20soft%20golden%20and%20warm%20pink%20accents%2C%20painterly%20digital%20art%20style%2C%20cinematic%20atmosphere%2C%20dreamy%20and%20immersive%20mood%2C%20no%20text%20no%20characters%20abstract%20artistic%20background&width=1600&height=900&seq=hero-bg-main-01&orientation=landscape"
            alt="樱花动漫网韩漫阅读平台背景"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-background-50"></div>
        </div>
        <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center py-20">
          <div className="animate-fade-in">
            <span className="inline-block px-4 py-1.5 text-xs font-medium text-primary-300 bg-primary-500/10 border border-primary-500/20 rounded-full mb-6">
              韩漫下拉式阅读 · 免费畅享
            </span>
          </div>
          <h1 id="hero-heading" className="animate-fade-in animation-delay-100 text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground-50 leading-tight mb-6">
            探索无限韩漫世界
            <br />
            <span className="text-primary-400">下拉之间 精彩呈现</span>
          </h1>
          <p className="animate-fade-in animation-delay-200 text-base md:text-lg text-foreground-200 max-w-2xl mx-auto mb-10 leading-relaxed">
            来到樱花动漫网，免费畅享海量韩漫资源。我们提供流畅的下拉式在线阅读体验，让每一页漫画都成为享受。恋爱、奇幻、动作、悬疑——你喜欢的都在这里。
          </p>
          <div className="animate-fade-in animation-delay-300 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/category/all"
              className="px-8 py-3.5 text-base font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-lg transition-all whitespace-nowrap cursor-pointer w-full sm:w-auto"
            >
              立即开始阅读
            </Link>
            <Link
              to="/about"
              className="px-8 py-3.5 text-base font-medium text-foreground-100 border border-foreground-100/30 hover:bg-white/10 rounded-lg transition-all whitespace-nowrap cursor-pointer w-full sm:w-auto"
            >
              了解更多
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative -mt-16 z-20 px-4 md:px-6" aria-label="平台数据">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { value: '12,000+', label: '韩漫资源', icon: 'ri-book-open-line' },
              { value: '500万+', label: '月活跃读者', icon: 'ri-user-heart-line' },
              { value: '99.9%', label: '服务可用率', icon: 'ri-shield-check-line' },
              { value: '每日更新', label: '最新章节同步', icon: 'ri-refresh-line' },
            ].map((stat, i) => (
              <div
                key={i}
                className="bg-background-50/95 backdrop-blur-sm border border-background-200/70 rounded-xl p-5 md:p-6 text-center hover:border-primary-300/50 transition-all"
              >
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-lg bg-primary-500/10">
                  <i className={`${stat.icon} text-primary-500 text-xl`}></i>
                </div>
                <div className="text-xl md:text-2xl font-heading font-bold text-foreground-900">{stat.value}</div>
                <div className="text-xs md:text-sm text-foreground-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Category Navigation */}
      <section className="py-16 md:py-20 px-4 md:px-6" aria-labelledby="category-heading">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 id="category-heading" className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">浏览漫画分类</h2>
            <p className="mt-3 text-foreground-500 text-sm md:text-base">探索不同类型的韩漫，找到你的最爱</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-4">
            {categories.filter(c => c.slug !== 'all').map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="flex flex-col items-center gap-2.5 p-5 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300/50 hover:bg-primary-500/5 transition-all group cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 text-accent-700 group-hover:bg-accent-200 transition-colors">
                  <i className={`${cat.icon} text-xl`}></i>
                </div>
                <span className="text-sm font-medium text-foreground-700 group-hover:text-foreground-900 transition-colors whitespace-nowrap">{cat.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Manga */}
      <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100/50" aria-labelledby="featured-heading">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 id="featured-heading" className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">精选推荐</h2>
              <p className="mt-2 text-sm text-foreground-500">评分最高的人气韩漫作品</p>
            </div>
            <Link to="/category/all" className="hidden sm:flex items-center gap-1 text-sm font-medium text-primary-500 hover:text-primary-400 transition-colors cursor-pointer whitespace-nowrap">
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop>
            {featuredMangas.map((manga) => (
              <Link
                key={manga.id}
                to={`/manga/${manga.id}`}
                className="group cursor-pointer"
              >
                <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200 mb-3">
                  <img
                    src={manga.coverImage}
                    alt={`${manga.title} 韩漫封面`}
                    title={`${manga.title} - 免费韩漫在线阅读`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-2 left-2">
                    <span className="px-2 py-0.5 text-[10px] font-medium bg-primary-500 text-background-50 rounded-md">
                      {manga.rating}
                    </span>
                  </div>
                  {manga.status === '连载中' && (
                    <div className="absolute top-2 right-2">
                      <span className="px-2 py-0.5 text-[10px] font-medium bg-accent-500 text-background-50 rounded-md">
                        连载
                      </span>
                    </div>
                  )}
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">{manga.title}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <StarRating rating={manga.rating} />
                  <span className="text-xs text-foreground-400">{manga.rating}</span>
                </div>
                <p className="text-xs text-foreground-400 mt-1">{manga.views.toLocaleString()} 次阅读</p>
              </Link>
            ))}
          </div>
          <Link to="/category/all" className="sm:hidden flex items-center justify-center gap-1 mt-6 text-sm font-medium text-primary-500 cursor-pointer whitespace-nowrap">
            查看全部漫画 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      {/* Latest Updates */}
      <section className="py-10 md:py-14 px-4 md:px-6" aria-labelledby="latest-heading">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 id="latest-heading" className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">最新更新</h2>
              <p className="mt-2 text-sm text-foreground-500">刚刚更新的热门章节</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {latestMangas.map((manga) => (
              <Link
                key={manga.id}
                to={`/manga/${manga.id}`}
                className="flex gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300/50 transition-all group cursor-pointer"
              >
                <div className="w-20 h-28 flex-shrink-0 rounded-lg overflow-hidden bg-background-200">
                  <img
                    src={manga.coverImage}
                    alt={`${manga.title} 最新更新`}
                    title={`${manga.title} 最新章节更新`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="flex flex-col justify-between min-w-0">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors truncate">{manga.title}</h3>
                    <p className="text-xs text-foreground-400 mt-1">{manga.altTitle}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {manga.tags.slice(0, 2).map((tag) => (
                        <span key={tag} className="px-2 py-0.5 text-[10px] bg-secondary-100 text-secondary-700 rounded-md whitespace-nowrap">{tag}</span>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[10px] text-foreground-400">更新于 {manga.updateDate}</span>
                    <span className="text-[10px] text-primary-500 font-medium whitespace-nowrap">立即阅读 →</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Banner CTA */}
      <section className="py-10 md:py-14 px-4 md:px-6" aria-labelledby="cta-heading">
        <div className="max-w-5xl mx-auto relative rounded-2xl overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20atmospheric%20abstract%20background%20with%20soft%20golden%20and%20peach%20gradients%2C%20subtle%20cherry%20blossom%20petal%20textures%2C%20dreamy%20bokeh%20effect%2C%20elegant%20and%20inviting%20digital%20art%2C%20no%20text%20no%20people%2C%20clean%20aesthetic&width=1200&height=400&seq=cta-banner-home-01&orientation=landscape"
              alt=""
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background-900/80 to-background-900/40"></div>
          </div>
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6 p-8 md:p-12">
            <div>
              <h3 id="cta-heading" className="text-xl md:text-2xl font-heading font-bold text-foreground-50">准备好踏上韩漫旅程了吗？</h3>
              <p className="mt-2 text-sm text-foreground-200">即刻加入数百万读者的行列，免费畅享海量韩漫。</p>
            </div>
            <Link
              to="/category/all"
              className="px-8 py-3 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-lg transition-all whitespace-nowrap cursor-pointer flex-shrink-0"
            >
              开始免费阅读
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100/50" aria-labelledby="testimonial-heading">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 id="testimonial-heading" className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">读者评价</h2>
            <p className="mt-2 text-sm text-foreground-500">听听他们怎么说</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {testimonials.map((t) => (
              <div key={t.id} className="p-6 rounded-xl bg-background-50 border border-background-200/70">
                <div className="flex items-center gap-3 mb-4">
                  <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                  <div>
                    <div className="text-sm font-semibold text-foreground-900">{t.name}</div>
                    <div className="text-xs text-foreground-400">{t.date}</div>
                  </div>
                </div>
                <StarRating rating={t.rating} />
                <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{t.comment}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-10 md:py-14 px-4 md:px-6" aria-labelledby="faq-heading">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 id="faq-heading" className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">常见问题</h2>
            <p className="mt-2 text-sm text-foreground-500">关于韩漫阅读的常见疑问</p>
          </div>

          {/* Schema.org FAQPage */}
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": faqs.map((faq) => ({
                  "@type": "Question",
                  "name": faq.question,
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": faq.answer
                  }
                }))
              })
            }}
          />

          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="rounded-xl border border-background-200/70 overflow-hidden">
                <button
                  className="w-full flex items-center justify-between p-5 text-left bg-background-50 hover:bg-background-100 transition-colors cursor-pointer"
                  onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                  aria-expanded={activeFaq === i}
                >
                  <span className="text-sm font-medium text-foreground-900 pr-4">{faq.question}</span>
                  <i className={`ri-${activeFaq === i ? 'subtract' : 'add'}-line text-foreground-400 flex-shrink-0 text-lg transition-transform duration-200 ${activeFaq === i ? 'rotate-180' : ''}`}></i>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    activeFaq === i ? 'max-h-60' : 'max-h-0'
                  }`}
                >
                  <p className="px-5 pb-5 text-sm text-foreground-500 leading-relaxed">{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Breadcrumb Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "首页",
                "item": "https://www.yinghuadongman.com/"
              }
            ]
          })
        }}
      />
    </>
  );
}